// ════════════════════════════════════════════════════════════════════════════
// LpaPageHeader — header semântico para páginas (com optional breadcrumb + actions)
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export interface LpaPageHeaderProps {
  title: string;
  /** Subtítulo descritivo (sans muted) — não usar UPPERCASE; texto natural */
  sub?: string;
  /** Breadcrumb opcional renderizado acima do título */
  breadcrumb?: React.ReactNode;
  /** Ações alinhadas à direita (botões, switchers, etc) */
  actions?: React.ReactNode;
  /** Tag semântica do título — default h1 */
  as?: "h1" | "h2";
  /** Estilo do sub — "prose" (sans muted, default) ou "eyebrow" (mono caps) */
  subStyle?: "prose" | "eyebrow";
  className?: string;
  style?: React.CSSProperties;
}

export default function LpaPageHeader({
  title,
  sub,
  breadcrumb,
  actions,
  as: Tag = "h1",
  subStyle = "prose",
  className,
  style,
}: LpaPageHeaderProps): React.ReactElement {
  return (
    <header
      className={className}
      style={{
        marginBottom: 28,
        paddingBottom: 20,
        borderBottom: "1px solid var(--lpa-border)",
        ...style,
      }}
    >
      {breadcrumb && <div style={{ marginBottom: 10 }}>{breadcrumb}</div>}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <Tag
            style={{
              fontFamily: '"Playfair Display", "Cormorant Garamond", "Garamond", Georgia, serif',
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "var(--lpa-text)",
              margin: 0,
            }}
          >
            {title}
          </Tag>
          {sub && (
            <div
              style={
                subStyle === "eyebrow"
                  ? {
                      fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--lpa-muted)",
                      marginTop: 8,
                    }
                  : {
                      fontFamily: 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif',
                      fontSize: 13,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      color: "var(--lpa-sub)",
                      marginTop: 6,
                    }
              }
            >
              {sub}
            </div>
          )}
        </div>
        {actions && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
